import React, { useState, useEffect, useRef } from "react";
import JSZip from "jszip";
import html2canvas from 'html2canvas';
import styled from "styled-components";
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import TextField from '@material-ui/core/TextField';

import { QRCodeSVG } from 'qrcode.react';

import { Flex, SecondaryHeader, SectionWrapper, FormCard, FieldGroup, Label, ActionToolbar, ToolbarButton, HelpTextInline } from "../styles/global";

import { requestNewQRCodes } from "../utils/api.utils";

import { QRCodeType } from '../models'

import maskIcon from "../assets/mask.svg";
import vaccineIcon from "../assets/vaccine.svg";
import testIcon from "../assets/test.svg";
import ppeIcon from "../assets/ppe.svg";
import foodIcon from "../assets/food.svg";
import medicationIcon from "../assets/medication.svg";
import quizIcon from "../assets/quiz.svg";
import sequencingIcon from "../assets/sequence.svg";
import defaultIcon from "../assets/default.svg";

interface Props {
  id: string;
}

interface QRProps {
  type: QRCodeType;
  code: string;
}

function chunkArray(arr: any[], by: number = 3) {
  let chunks = [],
    i = 0,
    n = arr.length;
  while (i < n) {
    chunks.push(arr.slice(i, i += by));
  }
  return chunks;
}

const pickIconType = (type: QRCodeType) => {
  switch (type) {
    case QRCodeType.MASK:
      return maskIcon
    case QRCodeType.VACCINE:
      return vaccineIcon
    case QRCodeType.TEST:
      return testIcon
    case QRCodeType.PPE:
      return ppeIcon
    case QRCodeType.FOOD:
      return foodIcon
    case QRCodeType.MEDICATION:
      return medicationIcon
    case QRCodeType.QUIZ:
      return quizIcon
    case QRCodeType.SEQUENCING:
      return sequencingIcon
    default:
      return defaultIcon
  }
}

export const QRCodes = ({ id }: Props) => {
  const [type, setType] = useState<any>(QRCodeType.VACCINE);
  const [count, setCount] = useState<any>(1);
  const [use_count, setUseCount] = useState<any>(1);
  const [info, setInfo] = useState<any>("");
  const [codes, setQRCodes] = useState<any[]>([]);
  const [progress, setProgress] = useState(0);

  const printRef = useRef<any>();

  const handleDownload = async () => {
    const link = document.createElement('a');
    let zip = new JSZip();
    const QRCollection = document.getElementsByClassName("qr-code");

    if (QRCollection.length === 0) return;

    setProgress(0);

    let i = 0;
    const processNext = () => {
      if (i >= QRCollection.length) {
        zip.generateAsync({ type: "base64" }).then(function (content: string) {
          link.href = "data:application/zip;base64," + content;
          link.download = `${type}_batch.zip`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          setTimeout(() => setProgress(0), 2000);
        });
        return;
      }

      let element = QRCollection.item(i) as HTMLElement;
      if (!element) return;

      // Use requestAnimationFrame to align with browser refresh cycles
      requestAnimationFrame(() => {
        html2canvas(element, {
          logging: false,
          useCORS: true,
          scale: 1.5, // High quality but optimized
          backgroundColor: '#ffffff',
          removeContainer: true // Helps with performance by cleaning up internal iframes faster
        }).then(function (canvas) {
          canvas.toBlob(function (blob) {
            zip.file(i + ".png", blob!);
            i++;

            const percent = Math.floor((i / QRCollection.length) * 100);
            setProgress(percent);

            // Small throttle to let the browser process reflows/UI updates
            setTimeout(processNext, 20);
          });
        });
      });
    };

    processNext();
  }

  const requestQRCodes = () => {
    setProgress(0);
    requestNewQRCodes(id,
      {
        type,
        count,
        info,
        use_count,
      })
      .then((result) => {
        const payload = result as any;
        setQRCodes(payload);
      })
      .catch((err) => setQRCodes([]));
  };


  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
      <SectionWrapper>
        <SecondaryHeader>Batch Generator</SecondaryHeader>
        <FormCard>
          <FieldGroup>
            <Label>QR Code Purpose</Label>
            <Select
              fullWidth
              required
              id="qr-type-select"
              variant="outlined"
              MenuProps={{ getContentAnchorEl: null }}
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <MenuItem value={QRCodeType.VACCINE}>Vaccine</MenuItem>
              <MenuItem value={QRCodeType.MASK}>Mask</MenuItem>
              <MenuItem value={QRCodeType.PPE}>Personal Protection Equipment</MenuItem>
              <MenuItem value={QRCodeType.TEST}>Diagnostic Test</MenuItem>
              <MenuItem value={QRCodeType.FOOD}>Food Item</MenuItem>
              <MenuItem value={QRCodeType.MEDICATION}>Medication</MenuItem>
              <MenuItem value={QRCodeType.QUIZ}>Quiz</MenuItem>
              <MenuItem value={QRCodeType.SEQUENCING}>Sequencing</MenuItem>
            </Select>
          </FieldGroup>

          <Flex gap="24px">
            <FieldGroup style={{ flex: 1 }}>
              <Label>Total Codes</Label>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                InputProps={{ inputProps: { min: 1, max: 100 } }}
                placeholder="e.g. 50"
                value={count}
                onChange={(e) => {
                  let val = parseInt(e.target.value);
                  if (val > 100) val = 100;
                  setCount(val || "");
                }}
              />
              <HelpTextInline>Up to 100 per batch</HelpTextInline>
            </FieldGroup>

            <FieldGroup style={{ flex: 1 }}>
              <Label>Uses Per Code</Label>
              <TextField
                fullWidth
                type="number"
                variant="outlined"
                InputProps={{ inputProps: { min: 1, max: 100 } }}
                placeholder="e.g. 1"
                value={use_count}
                onChange={(e) => {
                  let val = parseInt(e.target.value);
                  if (val > 100) val = 100;
                  setUseCount(val || "");
                }}
              />
              <HelpTextInline>Scan limit per code</HelpTextInline>
            </FieldGroup>
          </Flex>

          <FieldGroup>
            <Label>Additional Info Label (Optional)</Label>
            <TextField
              fullWidth
              variant="outlined"
              value={info}
              onChange={(e) => setInfo(e.target.value)}
            />
            {(type === QRCodeType.QUIZ || type === QRCodeType.FOOD) && (
              <InfoHelpBox>
                <InfoHelpIcon>ℹ</InfoHelpIcon>
                <InfoHelpContent>
                  {type === QRCodeType.QUIZ && (
                    <>
                      <InfoHelpTitle>Quiz QR Code</InfoHelpTitle>
                      <InfoHelpText>
                        Enter the <strong>Quiz ID</strong> (integer) to link this QR code to a specific quiz.
                        If the participant hasn't taken the quiz yet, scanning will unlock it.
                      </InfoHelpText>
                    </>
                  )}
                  {type === QRCodeType.FOOD && (
                    <>
                      <InfoHelpTitle>Food QR Code</InfoHelpTitle>
                      <InfoHelpText>
                        Set the contamination status using these values:
                      </InfoHelpText>
                      <InfoHelpList>
                        <li><code>0</code> — Not contaminated (safe food)</li>
                        <li><code>1</code> — Contaminated</li>
                        <li><code>2</code> — Contaminated with AMR bacteria</li>
                      </InfoHelpList>
                    </>
                  )}
                </InfoHelpContent>
              </InfoHelpBox>
            )}
          </FieldGroup>

          <ActionToolbar>
            <ToolbarButton
              variant="contained"
              color="primary"
              onClick={requestQRCodes}
            >
              Generate Batch
            </ToolbarButton>
            <ToolbarButton
              variant="outlined"
              disabled={codes.length === 0}
              onClick={handleDownload}
            >
              Download ZIP
            </ToolbarButton>
            {codes.length > 0 && (
              <ToolbarButton
                variant="text"
                style={{ color: '#e53e3e', marginLeft: 'auto' }}
                onClick={() => setQRCodes([])}
              >
                Clear Batch
              </ToolbarButton>
            )}
          </ActionToolbar>

          {codes.length > 0 && (
            <BatchInfo>
              <SuccessIcon>✓</SuccessIcon>
              <span>{codes.length} Codes Ready for Export</span>
            </BatchInfo>
          )}
        </FormCard>
      </SectionWrapper>

      <SectionWrapper>
        <SecondaryHeader>Generated Results</SecondaryHeader>
        <FormCard style={{ background: '#f8fafc' }}>
          <ProgressContainer>
            <Flex justify="space-between" alignItems="center" style={{ marginBottom: '12px' }}>
              <Label style={{ margin: 0 }}>Export Worker Status</Label>
              <ProgressText id="progress-percent">
                {progress > 0 && progress < 100 ? `Processing... ${progress}%` : progress === 100 ? 'Export Complete' : 'Idle'}
              </ProgressText>
            </Flex>
            <ProgressBarContainer>
              <ProgressBar id="progress-bar-obj" max="100" value={progress} />
            </ProgressBarContainer>
          </ProgressContainer>

          {codes && codes.length > 0 ? (
            <div style={{ marginTop: '24px' }}>
              <Label style={{ marginBottom: '20px' }}>Current Batch ({codes.length} items)</Label>
              <CodeContainer id="qr-codes" ref={printRef}>
                {chunkArray(codes).map((codeGroups, groupIndex) => (
                  <CodeRow key={groupIndex}>
                    {codeGroups.map(({ type, code }: QRProps) => {
                      return (
                        <CodeColumn key={code} className="qr-code">
                          <CodeIcon src={pickIconType(type)} alt={type} />
                          <CodeWrapper>
                            <QRCodeSVG size={180} value={`o2://${type}?q=${code}`} level="L" />
                          </CodeWrapper>
                          <div style={{ marginTop: '12px', textAlign: 'center', fontSize: '10px', color: '#a0aec0', fontFamily: 'monospace' }}>
                            {code}
                          </div>
                        </CodeColumn>
                      )
                    })}
                  </CodeRow>
                ))}
              </CodeContainer>
            </div>
          ) : (
            <Flex direction="column" alignItems="center" justify="center" style={{ padding: '60px 0', opacity: 0.5 }}>
              <p style={{ fontWeight: 600, color: '#718096' }}>No codes generated yet</p>
              <p style={{ fontSize: '13px', color: '#a0aec0' }}>Set your parameters above and click "Generate Batch"</p>
            </Flex>
          )}
        </FormCard>
      </SectionWrapper>
    </div>
  );
};

const BatchInfo = styled(Flex)`
  margin-top: 16px;
  color: #38a169;
  font-weight: 600;
  font-size: 14px;
  align-items: center;
  gap: 8px;
`;

const SuccessIcon = styled.div`
  width: 18px;
  height: 18px;
  background: #c6f6d5;
  color: #2f855a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
`;

const ProgressContainer = styled.div`
  margin-bottom: 24px;
`;

const ProgressText = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #718096;
  text-transform: uppercase;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: #edf2f7;
  border-radius: 10px;
  overflow: hidden;
`;

const ProgressBar = styled.progress`
  width: 100%;
  height: 100%;
  border: none;
  background: #edf2f7;
  
  &::-webkit-progress-bar {
    background: #edf2f7;
  }
  
  &::-webkit-progress-value {
    background: #3f51b5;
    border-radius: 10px;
    transition: width 0.3s ease;
  }
  
  &::-moz-progress-bar {
    background: #3f51b5;
    border-radius: 10px;
  }
`;

const CodeWrapper = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: center;
`;

const CodeColumn = styled.div`
  float: left;
  width: calc(33.33% - 12px);
  margin-right: 12px;
  margin-bottom: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-sizing: border-box;

  &:nth-child(3n) {
    margin-right: 0;
  }

  @media print {
    border-right: 1px dashed #e8e8e8;
    border-bottom: 1px dashed #e8e8e8;
    margin: 0;
    width: 33.33%;
    border-radius: 0;
  }
`;

const CodeRow = styled.div`
  &:after{
    content: "";
    display: table;
    clear: both;
  }
  ${CodeColumn}:nth-child(4){
    @media print {
      border-right: none;
    }
  }
`;

const CodeIcon = styled.img`
  padding-bottom: 24px;
  height: 60px;
  margin: 0 auto;
`;

const CodeContainer = styled.div`
  display: block;
  width: 100%;
  
  ${CodeRow}:nth-child(4n){
    break-after: always;
    page-break-after: always;
    & > ${CodeColumn}{
      @media print {
        border-bottom: none;
      }
    }
  }
`;

const InfoHelpBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 12px;
  padding: 16px;
  background: linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%);
  border: 1px solid #c7d2fe;
  border-radius: 10px;
  animation: fadeIn 0.2s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const InfoHelpIcon = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  background: #6366f1;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  margin-top: 2px;
`;

const InfoHelpContent = styled.div`
  flex: 1;
`;

const InfoHelpTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #4338ca;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  margin-bottom: 6px;
`;

const InfoHelpText = styled.p`
  font-size: 14px;
  color: #4c4f69;
  line-height: 1.5;
  margin: 0;
  
  strong {
    color: #312e81;
    font-weight: 600;
  }
`;

const InfoHelpList = styled.ul`
  margin: 8px 0 0 0;
  padding: 0 0 0 4px;
  list-style: none;
  
  li {
    font-size: 13px;
    color: #4c4f69;
    padding: 4px 0;
    display: flex;
    align-items: center;
    gap: 8px;
    
    code {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 24px;
      height: 22px;
      padding: 0 8px;
      background: #6366f1;
      color: white;
      border-radius: 4px;
      font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
      font-size: 12px;
      font-weight: 600;
    }
  }
`;
